import type {GraphQLError} from '@udemy-v2/graphql';

import {CourseAccessRestriction} from './constants';

export interface CourseAccessErrorPayload {
    restriction: CourseAccessRestriction;
    courseId?: string;
    courseTitle?: string;
    passwordError?: string;
    topicRedirectUrl?: string;
    topicId?: string;
    categoryId?: string;
    subcategoryId?: string;
}

/**
 * Thrown by `fetchCourseData` when the GraphQL `courseByLandingURL` response
 * contains an `ACCESS_RESTRICTED` error extension. Callers branch on
 * `restriction` to render the right limited-access UI.
 */
export class CourseAccessError extends Error {
    readonly restriction: CourseAccessRestriction;
    readonly courseId?: string;
    readonly courseTitle?: string;
    readonly passwordError?: string;
    readonly topicRedirectUrl?: string;
    readonly topicId?: string;
    readonly categoryId?: string;
    readonly subcategoryId?: string;

    constructor(payload: CourseAccessErrorPayload) {
        super(`Course access restricted: ${payload.restriction}`);
        this.name = 'CourseAccessError';
        this.restriction = payload.restriction;
        this.courseId = payload.courseId;
        this.courseTitle = payload.courseTitle;
        this.passwordError = payload.passwordError;
        this.topicRedirectUrl = payload.topicRedirectUrl;
        this.topicId = payload.topicId;
        this.categoryId = payload.categoryId;
        this.subcategoryId = payload.subcategoryId;
    }
}

/**
 * Maps a GraphQL `errors[]` array onto a typed `CourseAccessError` when
 * the array contains an entry with the `ACCESS_RESTRICTED` extension shape
 * (`extensions.code === 'ACCESS_RESTRICTED'` and `extensions.courseAccessRestriction`
 * set to one of the four known variants).
 *
 * Returns `null` when the array is missing/empty, contains no
 * `ACCESS_RESTRICTED` entry, or the entry carries an unknown restriction
 * value — callers can treat that as "no access error" and surface whatever
 * partial data the response did return.
 *
 * The fetcher (`@udemy-v2/graphql` v2.36+ under `ALLOW_PARTIAL`) injects
 * `errors` onto the resolved response alongside the data fields, so both
 * the server-side `fetchCourseData` and the client-side refetch in
 * `LimitedAccessCLPWithClientFallback` can read the same field and call
 * this mapper on it.
 */
export function toCourseAccessError(
    errors: readonly GraphQLError[] | undefined,
): CourseAccessError | null {
    if (!errors || errors.length === 0) {
        return null;
    }
    const accessRestrictedEntry = errors.find(
        (entry) => entry.extensions?.code === 'ACCESS_RESTRICTED',
    );
    if (!accessRestrictedEntry) {
        return null;
    }
    const extensions = accessRestrictedEntry.extensions as Record<string, unknown>;
    const restriction = extensions.courseAccessRestriction;
    if (
        restriction !== CourseAccessRestriction.PASSWORD_PROTECTED &&
        restriction !== CourseAccessRestriction.INVITE_ONLY &&
        restriction !== CourseAccessRestriction.UNAVAILABLE &&
        restriction !== CourseAccessRestriction.UNAVAILABLE_IN_YOUR_ORGANIZATION
    ) {
        return null;
    }
    const rawCourseId = extensions.courseId;
    const coerceId = (raw: unknown): string | undefined =>
        typeof raw === 'string' || typeof raw === 'number' ? String(raw) : undefined;
    return new CourseAccessError({
        restriction,
        courseId: coerceId(rawCourseId),
        courseTitle:
            typeof extensions.courseTitle === 'string' ? extensions.courseTitle : undefined,
        passwordError:
            typeof extensions.passwordError === 'string' ? extensions.passwordError : undefined,
        topicRedirectUrl:
            typeof extensions.topicRedirectUrl === 'string'
                ? extensions.topicRedirectUrl
                : undefined,
        topicId: coerceId(extensions.topicId),
        categoryId: coerceId(extensions.categoryId),
        subcategoryId: coerceId(extensions.subcategoryId),
    });
}
